// Per-child voice volume for Pip (parent-set, 10-100%). Seeded from the
// child document on page load and updated instantly when the parent moves
// the slider; localStorage keeps it sticky across reloads before the next
// children fetch.
const KEY = (childId) => `pip_volume_${childId}`;
const mem = new Map();
const quietMem = new Map(); // childId -> quiet_hours ({enabled,start,end})
const WHISPER = 30; // % — wind-down volume in the hour before quiet hours

export const setPipVolume = (childId, v) => {
  const vol = Math.min(100, Math.max(10, Number(v) || 100));
  mem.set(childId, vol);
  try {
    localStorage.setItem(KEY(childId), String(vol));
  } catch {
    /* private mode */
  }
};

export const getPipVolume = (childId) => {
  if (mem.has(childId)) return mem.get(childId);
  try {
    const v = Number(localStorage.getItem(KEY(childId)));
    if (v >= 10 && v <= 100) {
      mem.set(childId, v);
      return v;
    }
  } catch {
    /* private mode */
  }
  return 100;
};

// Convenience: 0..1 gain multiplier for Web Audio graphs. Auto-drops to a
// whisper in the 60 minutes before the child's quiet hours begin, so play
// winds down calmly before bedtime.
export const pipGain = (childId) => {
  let v = getPipVolume(childId);
  const qh = quietMem.get(childId);
  if (qh?.enabled) {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const untilQuiet = ((qh.start * 60) - mins + 1440) % 1440;
    if (untilQuiet > 0 && untilQuiet <= 60) v = Math.min(v, WHISPER);
  }
  return v / 100;
};

// --- Voice speed (Pip's global talking pace): 0.75x / 1x / 1.25x ----------
export const VOICE_SPEEDS = [0.75, 1, 1.25];
const SPEED_KEY = (childId) => `pip_speed_${childId}`;
const speedMem = new Map();

export const setVoiceSpeed = (childId, s) => {
  const speed = VOICE_SPEEDS.includes(Number(s)) ? Number(s) : 1;
  speedMem.set(childId, speed);
  try {
    localStorage.setItem(SPEED_KEY(childId), String(speed));
  } catch {
    /* private mode */
  }
};

export const getVoiceSpeed = (childId) => {
  if (speedMem.has(childId)) return speedMem.get(childId);
  try {
    const s = Number(localStorage.getItem(SPEED_KEY(childId)));
    if (VOICE_SPEEDS.includes(s)) {
      speedMem.set(childId, s);
      return s;
    }
  } catch {
    /* private mode */
  }
  return 1;
};

// Seed from a child document (children API includes pip_volume when set).
export const seedPipVolume = (child) => {
  if (!child?.child_id) return;
  if (child.pip_volume) setPipVolume(child.child_id, child.pip_volume);
  if (child.voice_speed) setVoiceSpeed(child.child_id, child.voice_speed);
  if (child.quiet_hours) quietMem.set(child.child_id, child.quiet_hours);
};

// --- Safari habitat ambience volume (parent-set, 0-100; 0 mutes the bed) ---
const AMB_KEY = (childId) => `ambience_volume_${childId}`;
const ambMem = new Map();

export const setAmbienceVolume = (childId, v) => {
  const vol = Math.min(100, Math.max(0, Number(v) || 0));
  ambMem.set(childId, vol);
  try { localStorage.setItem(AMB_KEY(childId), String(vol)); } catch { /* private mode */ }
};

export const getAmbienceVolume = (childId) => {
  if (ambMem.has(childId)) return ambMem.get(childId);
  try {
    const raw = localStorage.getItem(AMB_KEY(childId));
    if (raw !== null) {
      const v = Number(raw);
      if (v >= 0 && v <= 100) { ambMem.set(childId, v); return v; }
    }
  } catch { /* private mode */ }
  return 50; // default: gentle background
};

export const seedAmbienceVolume = (child) => {
  if (child?.child_id && child.ambience_volume != null) {
    setAmbienceVolume(child.child_id, child.ambience_volume);
  }
};
